import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  ButtonGroup,
  Image,
  Badge,
  Icon,
  SimpleGrid,
  Skeleton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import {
  FaShoppingCart,
  FaTrash,
  FaShieldAlt,
  FaArrowRight,
  FaCheckCircle,
  FaLock,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "../Context/CartContext";
import { Authcontext } from "../Context/Authcontext";
import { apiUrl } from "../ApiUrl/ApiUrl";

function CartPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const { cartData = [], getData, loading } = useContext(CartContext);
  const { correct } = useContext(Authcontext);

  const [deleteId, setDeleteId] = useState(null);
  const [totalDescPrice, setTotalDescPrice] = useState(0);
  const [totalOrigPrice, setTotalOrigPrice] = useState(0);
  const [gst, setGst] = useState(0);
  const [uemail, setUemail] = useState("");
  const [name, setName] = useState("");

  const uid = localStorage.getItem("uid");

  const fetchPrice = async () => {
    const currentUid = localStorage.getItem("uid");
    if (!correct || !currentUid) {
      setTotalDescPrice(0);
      setGst(0);
      setTotalOrigPrice(0);
      return;
    }
    try {
      const res = await fetch(`${apiUrl}/public/getAllCartById?uid=${encodeURIComponent(currentUid)}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        const total = data.reduce((sum, item) => sum + parseFloat(item.descprice || 0), 0);
        const totalG = data.reduce((sum, item) => sum + parseFloat(item.gst || 0), 0);
        const origTotal = data.reduce(
          (sum, item) => sum + (item.quantity || 1) * parseFloat(item.originalprice || 0),
          0
        );
        setTotalDescPrice(total);
        setGst(totalG);
        setTotalOrigPrice(origTotal);
      } else {
        setTotalDescPrice(0);
        setGst(0);
        setTotalOrigPrice(0);
      }
    } catch (err) {
      console.error("Error fetching price details:", err);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (correct && uid) {
      getData(uid);
      fetchPrice();
    } else {
      setTotalDescPrice(0);
      setGst(0);
      setTotalOrigPrice(0);
    }
  }, [correct, uid]);

  useEffect(() => {
    if (correct && uid) {
      fetch(`${apiUrl}/public/getUserById?uid=${encodeURIComponent(uid)}`)
        .then((res) => res.json())
        .then((res) => {
          if (res) {
            setUemail(res.username || "");
            setName(res.name || "");
            localStorage.setItem("name", res.name || "");
            localStorage.setItem("email", res.username || "");
          }
        })
        .catch((err) => console.error("Error fetching user data:", err));
    } else {
      setUemail("");
      setName("");
    }
  }, [correct, uid]);

  const handlePatchLess = async (cartid) => {
    try {
      await fetch(`${apiUrl}/public/cartDecreament?cid=${cartid}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      await getData();
      await fetchPrice();
    } catch (err) {
      console.error("Error decrementing quantity:", err);
    }
  };

  const handlePatchAdd = async (cartid) => {
    try {
      await fetch(`${apiUrl}/public/cartIncreament?cid=${cartid}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      await getData();
      await fetchPrice();
    } catch (err) {
      console.error("Error incrementing quantity:", err);
    }
  };

  const promptDelete = (cid) => {
    setDeleteId(cid);
    onOpen();
  };

  const confirmDelete = async () => {
    onClose();
    if (!deleteId) return;

    try {
      await fetch(`${apiUrl}/public/deleteByCid?cid=${deleteId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      toast({
        title: "Item Removed",
        status: "info",
        duration: 2000,
        position: "top",
      });
      await getData();
      await fetchPrice();
    } catch (err) {
      console.error("Error removing item:", err);
    }
  };

  const sellingPrice = totalOrigPrice;
  const discount = Math.max(0, totalOrigPrice - totalDescPrice);
  const totalAmount = sellingPrice - discount + gst;

  const handlePlaceOrder = () => {
    if (!correct) {
      toast({
        title: "Please Sign In",
        description: "You must be logged in to proceed to checkout.",
        status: "warning",
        duration: 3000,
        position: "top",
      });
      return;
    }

    const orderId = Math.random().toString(36).substr(2, 9).toUpperCase();
    localStorage.setItem("order_id", orderId);

    fetch(`${apiUrl}/public/saveorder`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        order_id: orderId,
        amount: totalAmount,
        customer_id: uid,
        customer_email: uemail,
        customer_phone: "0000",
        payment_page_client_id: "42962",
        action: "paymentPage",
        status: "PENDING",
        return_url: "https://navneetlawstudio.com/payment-callback",
        description: "Complete your payment",
        first_name: name,
        last_name: "Customer",
        cid: uid,
      }),
    }).catch((err) => console.error("Error pre-saving order:", err));

    navigate("/delivery");
  };

  // Loading State
  if (loading) {
    return (
      <Box maxW="1360px" mx="auto" px={{ base: "4", md: "8" }} py="10">
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing="8">
          <Box gridColumn={{ lg: "span 2" }}>
            <Skeleton height="40px" mb="4" rounded="lg" />
            <Skeleton height="140px" mb="4" rounded="xl" />
            <Skeleton height="140px" rounded="xl" />
          </Box>
          <Box>
            <Skeleton height="320px" rounded="2xl" />
          </Box>
        </SimpleGrid>
      </Box>
    );
  }

  // Empty State
  if (!cartData || cartData.length === 0) {
    return (
      <Box minH="75vh" bg="#F8FAFC" py={{ base: "12", md: "20" }} px="4">
        <Box
          maxW="540px"
          mx="auto"
          bg="white"
          rounded="2xl"
          p={{ base: "8", md: "12" }}
          textAlign="center"
          border="1px solid #E2E8F0"
          shadow="sm"
        >
          <Box
            w="80px"
            h="80px"
            mx="auto"
            bg="blue.50"
            color="#2563EB"
            rounded="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb="4"
          >
            <Icon as={FaShoppingCart} boxSize="36px" />
          </Box>
          <Text fontSize="22px" fontWeight="800" color="gray.900" mb="2">
            Your Cart is Currently Empty
          </Text>
          <Text fontSize="14px" color="gray.500" maxW="380px" mx="auto" mb="6">
            Looks like you haven't added any legal publications, bare acts, or software licenses yet.
          </Text>
          <Link to="/products?maincat=Books">
            <Button
              bg="#0F172A"
              color="white"
              size="lg"
              rounded="xl"
              fontWeight="700"
              px="8"
              _hover={{ bg: "#1E293B", shadow: "md" }}
            >
              Explore Catalog
            </Button>
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box minH="85vh" bg="#F8FAFC" py={{ base: "4", md: "8" }} px={{ base: "3", md: "6", lg: "8" }}>
      <Box maxW="1360px" mx="auto">
        {/* Breadcrumb */}
        <Breadcrumb spacing="8px" separator="›" fontSize="13px" color="gray.500" mb="6">
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/">Home</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink fontWeight="bold" color="gray.800">Shopping Cart</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Stepper Progress */}
        <Flex
          bg="white"
          p="3.5"
          rounded="xl"
          border="1px solid #E2E8F0"
          shadow="sm"
          mb="6"
          justify="space-around"
          align="center"
          fontSize="13px"
        >
          <Flex align="center" gap="2" color="#2563EB" fontWeight="bold">
            <Box w="22px" h="22px" rounded="full" bg="#2563EB" color="white" display="flex" alignItems="center" justifyContent="center" fontSize="11px">
              1
            </Box>
            <Text>Shopping Cart</Text>
          </Flex>
          <Box w="20px" h="2px" bg="gray.200" />
          <Flex align="center" gap="2" color="gray.400">
            <Box w="22px" h="22px" rounded="full" bg="gray.200" color="gray.600" display="flex" alignItems="center" justifyContent="center" fontSize="11px">
              2
            </Box>
            <Text display={{ base: "none", sm: "block" }}>Delivery Address</Text>
          </Flex>
          <Box w="20px" h="2px" bg="gray.200" />
          <Flex align="center" gap="2" color="gray.400">
            <Box w="22px" h="22px" rounded="full" bg="gray.200" color="gray.600" display="flex" alignItems="center" justifyContent="center" fontSize="11px">
              3
            </Box>
            <Text display={{ base: "none", sm: "block" }}>Order Review</Text>
          </Flex>
          <Box w="20px" h="2px" bg="gray.200" />
          <Flex align="center" gap="2" color="gray.400">
            <Box w="22px" h="22px" rounded="full" bg="gray.200" color="gray.600" display="flex" alignItems="center" justifyContent="center" fontSize="11px">
              4
            </Box>
            <Text display={{ base: "none", sm: "block" }}>Payment</Text>
          </Flex>
        </Flex>

        {/* 2-Column Cart Layout */}
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={{ base: 6, lg: 8 }} alignItems="start">
          {/* Left Column: Cart Items List */}
          <Box gridColumn={{ lg: "span 2" }}>
            <Flex justify="space-between" align="center" mb="3">
              <Text fontSize="18px" fontWeight="800" color="gray.900">
                Cart Items ({cartData.length})
              </Text>
              <Link to="/products?maincat=Books">
                <Text fontSize="13px" fontWeight="bold" color="#2563EB" _hover={{ textDecoration: "underline" }}>
                  + Add More Publications
                </Text>
              </Link>
            </Flex>

            <Flex direction="column" gap="4">
              {cartData.map((item) => {
                const itemOriginal = item.quantity * parseInt(item.originalprice || item.descprice || 0, 10);
                const itemDesc = parseFloat(item.descprice || 0);
                const discountPct =
                  itemOriginal > itemDesc
                    ? Math.round(((itemOriginal - itemDesc) / itemOriginal) * 100)
                    : 0;

                return (
                  <Box
                    key={item.id || item.cartid}
                    bg="white"
                    border="1px solid #E2E8F0"
                    rounded="xl"
                    p="4"
                    shadow="sm"
                    transition="all 0.2s"
                    _hover={{ borderColor: "blue.300" }}
                  >
                    <Flex direction={{ base: "column", sm: "row" }} gap="4">
                      {/* Product Thumbnail */}
                      <Box
                        w={{ base: "100%", sm: "110px" }}
                        h="120px"
                        bg="#F8FAFC"
                        rounded="lg"
                        p="2"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Image
                          src={item.imagePath}
                          alt={item.booktitle}
                          maxH="100%"
                          maxW="100%"
                          objectFit="contain"
                          fallbackSrc="https://navneetlawstudio.com/logo.png"
                        />
                      </Box>

                      {/* Item Details */}
                      <Box flex="1" display="flex" flexDirection="column" justify="space-between">
                        <Box>
                          <Text fontSize="15px" fontWeight="700" color="gray.900" lineHeight="1.3" mb="1">
                            {item.booktitle}
                          </Text>

                          {item.description && (
                            <Text fontSize="12.5px" color="gray.500" noOfLines={1} mb="2">
                              {item.description}
                            </Text>
                          )}

                          {/* Price Row */}
                          <Flex align="baseline" gap="2" wrap="wrap" mb="3">
                            <Text fontSize="18px" fontWeight="800" color="gray.900">
                              ₹{item.descprice}
                            </Text>
                            {itemOriginal > itemDesc && (
                              <>
                                <Text fontSize="13px" color="gray.400" as="del">
                                  ₹{itemOriginal}
                                </Text>
                                <Badge colorScheme="green" fontSize="11px" px="1.5" py="0.2" rounded="md">
                                  {discountPct}% OFF
                                </Badge>
                              </>
                            )}
                          </Flex>
                        </Box>

                        {/* Quantity Stepper & Remove Action */}
                        <Flex align="center" justify="space-between" pt="2" borderTop="1px solid #F1F5F9">
                          <Flex align="center" gap="2">
                            <Text fontSize="12.5px" fontWeight="600" color="gray.600">
                              Quantity:
                            </Text>
                            <ButtonGroup size="xs" isAttached variant="outline">
                              <Button
                                isDisabled={item.quantity <= 1}
                                onClick={() => handlePatchLess(item.cartid)}
                                px="2.5"
                              >
                                -
                              </Button>
                              <Button px="3" cursor="default" _hover={{ bg: "transparent" }}>
                                {item.quantity}
                              </Button>
                              <Button
                                onClick={() => handlePatchAdd(item.cartid)}
                                px="2.5"
                              >
                                +
                              </Button>
                            </ButtonGroup>
                          </Flex>

                          <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            leftIcon={<Icon as={FaTrash} boxSize="12px" />}
                            onClick={() => promptDelete(item.cartid)}
                            fontSize="12.5px"
                          >
                            Remove
                          </Button>
                        </Flex>
                      </Box>
                    </Flex>
                  </Box>
                );
              })}
            </Flex>
          </Box>

          {/* Right Column: Price Details & Place Order */}
          <Box position={{ lg: "sticky" }} top="90px">
            <Box bg="white" border="1px solid #E2E8F0" rounded="2xl" p="5" shadow="sm" mb="4">
              <Text fontSize="15px" fontWeight="700" color="gray.900" borderBottom="1px solid #F1F5F9" pb="3" mb="4">
                Price Breakdown
              </Text>

              <Flex direction="column" gap="3" fontSize="13.5px">
                <Flex justify="space-between">
                  <Text color="gray.600">Total MRP ({cartData.length} items)</Text>
                  <Text fontWeight="600" color="gray.900">₹{sellingPrice}</Text>
                </Flex>

                <Flex justify="space-between">
                  <Text color="gray.600">Catalog Discount</Text>
                  <Text fontWeight="600" color="green.600">- ₹{discount}</Text>
                </Flex>

                <Flex justify="space-between">
                  <Text color="gray.600">Applicable GST / Taxes</Text>
                  <Text fontWeight="600" color="gray.900">₹{gst}</Text>
                </Flex>

                <Flex justify="space-between">
                  <Text color="gray.600">Shipping & Handling</Text>
                  <Badge colorScheme="green" variant="subtle" fontSize="11px">FREE</Badge>
                </Flex>

                <Box borderTop="1px dashed #CBD5E1" pt="3" mt="1">
                  <Flex justify="space-between" align="baseline">
                    <Text fontSize="16px" fontWeight="800" color="gray.900">Total Amount</Text>
                    <Text fontSize="20px" fontWeight="800" color="#2563EB">₹{totalAmount}</Text>
                  </Flex>
                </Box>
              </Flex>

              {discount > 0 && (
                <Box mt="4" p="2.5" rounded="lg" bg="green.50" border="1px solid #BBF7D0">
                  <Text fontSize="12px" fontWeight="600" color="green.700" textAlign="center">
                    🎉 You will save ₹{discount} on this purchase!
                  </Text>
                </Box>
              )}

              {/* Checkout CTA */}
              <Button
                mt="5"
                w="100%"
                size="lg"
                bg="linear-gradient(135deg, #EAB308 0%, #CA8A04 100%)"
                color="gray.950"
                rounded="xl"
                fontSize="15px"
                fontWeight="800"
                rightIcon={<Icon as={FaArrowRight} />}
                _hover={{ filter: "brightness(1.08)", shadow: "md" }}
                onClick={handlePlaceOrder}
              >
                Proceed to Delivery
              </Button>
            </Box>

            {/* Trust Badge */}
            <Flex align="center" gap="3" p="3.5" bg="white" border="1px solid #E2E8F0" rounded="xl" shadow="sm">
              <Icon as={FaLock} color="green.500" boxSize="18px" />
              <Box>
                <Text fontSize="12.5px" fontWeight="700" color="gray.800">
                  Safe & Secure Checkout
                </Text>
                <Text fontSize="11px" color="gray.500">
                  Encrypted transactions with HDFC & Razorpay
                </Text>
              </Box>
            </Flex>
          </Box>
        </SimpleGrid>
      </Box>

      {/* Delete Item Confirmation Modal */}
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
        <AlertDialogOverlay bg="blackAlpha.600" backdropFilter="blur(4px)">
          <AlertDialogContent rounded="2xl" p="2">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove Publication
            </AlertDialogHeader>
            <AlertDialogBody fontSize="14px" color="gray.600">
              Are you sure you want to remove this item from your shopping cart?
            </AlertDialogBody>
            <AlertDialogFooter gap="2">
              <Button ref={cancelRef} onClick={onClose} rounded="lg" size="sm">
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} rounded="lg" size="sm">
                Remove Item
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default CartPage;
